import { useEffect, useMemo, useRef, useState } from 'react';

interface UsePaginationOptions<T> {
  items: T[];
  pageSize: number;
  selectedItemIndex?: number;
}

export const usePagination = <T>({
  items,
  pageSize,
  selectedItemIndex,
}: UsePaginationOptions<T>) => {
  const [page, setPage] = useState(1);
  const pageRef = useRef(page);
  const prevTotalRef = useRef(items.length);
  const total = items.length;
  const totalPages = Math.ceil(total / pageSize);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [page, pageSize, items]);

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, total);

  const handlePrev = () => setPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () => setPage((prev) => Math.min(prev + 1, totalPages));

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
    pageRef.current = page;
  }, [page, totalPages]);

  useEffect(() => {
    if (selectedItemIndex && selectedItemIndex > -1) {
      const selectedItemPage = Math.ceil((selectedItemIndex + 1) / pageSize);
      if (selectedItemPage !== pageRef.current) {
        setPage(selectedItemPage);
      }
    }
  }, [items, pageSize, selectedItemIndex]);

  useEffect(() => {
    if (!selectedItemIndex && total > prevTotalRef.current) {
      setPage(totalPages);
    }
    prevTotalRef.current = total;
  }, [selectedItemIndex, total, totalPages]);

  return {
    page,
    total,
    totalPages,
    startIndex,
    endIndex,
    paginatedItems,
    handlePrev,
    handleNext,
    setPage,
  };
};
